import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NUTRIENT_EMOJIS } from '@shared/config'
import { notyError } from '@shared/lib/notifications'
import { getDayJs } from '@shared/lib/utils'
import { Btn } from '@shared/ui/btn'
import { NotchLoader } from '@shared/ui/loader'

import { getMealsForDate } from '../api'

import styles from './styles.module.scss'

//
//

const dayjs = getDayJs()

const DATE_FORMAT = 'YYYY-MM-DD'

// days shown at once in the quick-select strip
const VISIBLE_DAYS = 5

const MEAL_TYPE_ICONS = {
  breakfast: 'fa-mug-hot',
  morning_snack: 'fa-apple-whole',
  lunch: 'fa-bowl-food',
  afternoon_snack: 'fa-cookie-bite',
  dinner: 'fa-utensils',
  evening_snack: 'fa-moon',
  unknown: 'fa-utensils',
}

const MACRO_KEYS = ['proteins', 'fats', 'carbs']

// macro key -> field in mealData.info
const INFO_KEYS = {
  calories: 'totalCalories',
  proteins: 'totalProteins',
  fats: 'totalFats',
  carbs: 'totalCarbs',
}

//
//

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
}

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: 'easeOut' },
  },
}

//
//

const round1 = (value) => Math.round(value * 10) / 10

const getInfoValue = (meal, key) =>
  Number(meal?.mealData?.info?.[INFO_KEYS[key]]) || 0

//
//

const FCMealsList = () => {
  const { t } = useTranslation()

  const [selectedDate, setSelectedDate] = useState(() =>
    dayjs().format(DATE_FORMAT)
  )

  // last day of the visible strip window
  const [windowEnd, setWindowEnd] = useState(() => dayjs().format(DATE_FORMAT))

  // tagged with the requested date, so a date change reads as loading
  const [meals, setMeals] = useState(null)

  //
  //

  useEffect(() => {
    let cancelled = false

    const fn = async () => {
      const mealsData = await getMealsForDate({ date: selectedDate })

      if (cancelled) {
        return
      }

      if (mealsData?.error) {
        notyError(t('mealsList:error.meals'))
        setMeals({ date: selectedDate, error: true })
        return
      }

      setMeals({ date: selectedDate, list: mealsData?.data || [] })
    }

    fn()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate])

  //
  //

  const stripDays = useMemo(() => {
    const end = dayjs(windowEnd)

    return Array.from({ length: VISIBLE_DAYS }, (_, i) => {
      const day = end.subtract(VISIBLE_DAYS - 1 - i, 'day')

      return {
        key: day.format(DATE_FORMAT),
        weekday: day.day(),
        number: day.format('D'),
      }
    })
  }, [windowEnd])

  const sortedMeals = useMemo(() => {
    if (!meals?.list) {
      return []
    }

    return [...meals.list].sort(
      (a, b) => new Date(b?.addedAt) - new Date(a?.addedAt)
    )
  }, [meals])

  const totals = useMemo(
    () =>
      sortedMeals.reduce(
        (acc, meal) => ({
          calories: round1(acc.calories + getInfoValue(meal, 'calories')),
          proteins: round1(acc.proteins + getInfoValue(meal, 'proteins')),
          fats: round1(acc.fats + getInfoValue(meal, 'fats')),
          carbs: round1(acc.carbs + getInfoValue(meal, 'carbs')),
        }),
        { calories: 0, proteins: 0, fats: 0, carbs: 0 }
      ),
    [sortedMeals]
  )

  //
  //

  const todayKey = dayjs().format(DATE_FORMAT)

  const dateLabel =
    selectedDate === todayKey
      ? t('mealsList:today')
      : dayjs(selectedDate).format('DD.MM.YYYY')

  const isLoading = meals?.date !== selectedDate

  // ISO dates compare correctly as strings
  const canGoNext = windowEnd < todayKey

  //
  //

  const goPrev = () => {
    setWindowEnd(
      dayjs(windowEnd).subtract(VISIBLE_DAYS, 'day').format(DATE_FORMAT)
    )
  }

  const goNext = () => {
    const next = dayjs(windowEnd).add(VISIBLE_DAYS, 'day')

    setWindowEnd(
      next.isAfter(dayjs(), 'day') ? todayKey : next.format(DATE_FORMAT)
    )
  }

  const onDateInput = (e) => {
    const { value } = e.target

    if (!value) {
      return
    }

    setSelectedDate(value)

    // bring the picked day into the visible window, roughly centered
    const target = dayjs(value).add(Math.floor(VISIBLE_DAYS / 2), 'day')

    setWindowEnd(
      target.isAfter(dayjs(), 'day') ? todayKey : target.format(DATE_FORMAT)
    )
  }

  //
  //

  let content

  if (isLoading) {
    content = (
      <div className="d-flex justify-content-center py-4">
        <NotchLoader size={35} />
      </div>
    )
  } else if (meals.error) {
    content = <p className={styles.list__error}>{t('mealsList:error.meals')}</p>
  } else if (!sortedMeals.length) {
    content = (
      <p className={styles.list__empty}>
        <i className="fa-solid fa-plate-wheat" />
        {t('mealsList:empty')}
      </p>
    )
  } else {
    content = (
      <motion.div
        key={meals.date}
        variants={listVariants}
        initial="hidden"
        animate="visible"
      >
        <ul className={styles.rows}>
          {sortedMeals.map((meal) => {
            const mealType = MEAL_TYPE_ICONS[meal?.possibleMealType]
              ? meal.possibleMealType
              : 'unknown'

            return (
              <motion.li key={meal.id} variants={rowVariants}>
                <Btn
                  design={['wrapper']}
                  className={styles.row}
                  link={`/meal/${meal.id}`}
                >
                  <span className={styles.row__icon}>
                    <i
                      className={clsx('fa-solid', MEAL_TYPE_ICONS[mealType])}
                    />
                  </span>

                  <span className={styles.row__main}>
                    <span className={styles.row__title}>
                      {meal?.mealTitle || t(`mealsList:mealTypes.${mealType}`)}
                    </span>

                    <span className={styles.row__sub}>
                      <span>{dayjs(meal?.addedAt).format('HH:mm')}</span>

                      {MACRO_KEYS.map((key) => (
                        <span key={key}>
                          {/* <i
                            style={{ backgroundColor: NUTRIENT_COLORS[key] }}
                          /> */}
                          <i>{NUTRIENT_EMOJIS[key]}</i>
                          {getInfoValue(meal, key)}
                        </span>
                      ))}
                    </span>
                  </span>

                  <span className={styles.row__kcal}>
                    {getInfoValue(meal, 'calories')}
                    <small>{t('mealsList:units.kcal')}</small>
                  </span>

                  <i
                    className={clsx(
                      'fa-solid fa-chevron-right',
                      styles.row__chevron
                    )}
                  />
                </Btn>
              </motion.li>
            )
          })}
        </ul>

        {/* ************************************************************************** */}

        <motion.div className={styles.total} variants={rowVariants}>
          <span className={styles.total__label}>{t('mealsList:total')}</span>

          <span className={styles.total__values}>
            <div className={styles['total__values-macros']}>
              {MACRO_KEYS.map((key) => (
                <span key={key}>
                  {t(`mealsList:macroShort.${key}`)} <b>{totals[key]}</b>
                </span>
              ))}
            </div>

            <span>
              <b>{totals.calories}</b> {t('mealsList:units.kcal')}
            </span>
          </span>
        </motion.div>
      </motion.div>
    )
  }

  //
  //

  return (
    <section className={styles.list}>
      <div className={styles.list__header}>
        <h2>{t('mealsList:title')}</h2>

        <div className={styles.picker}>
          <i className="fa-solid fa-calendar-days" />
          <span>{dateLabel}</span>

          <input
            type="date"
            value={selectedDate}
            max={todayKey}
            onChange={onDateInput}
            aria-label={t('mealsList:pickDate')}
          />
        </div>
      </div>

      {/* ************************************************************************** */}

      <div className={styles.days}>
        <Btn
          design={['wrapper']}
          className={styles.days__arrow}
          onClick={goPrev}
        >
          <i className="fa-solid fa-chevron-left" />
        </Btn>

        {stripDays.map((day) => (
          <Btn
            key={day.key}
            design={['wrapper']}
            className={clsx(styles.day, {
              [styles.day_active]: day.key === selectedDate,
            })}
            onClick={() => setSelectedDate(day.key)}
          >
            <span className={styles.day__weekday}>
              {t(`mealsList:weekdays.${day.weekday}`)}
            </span>
            <span className={styles.day__number}>{day.number}</span>
          </Btn>
        ))}

        <Btn
          design={['wrapper']}
          className={styles.days__arrow}
          onClick={goNext}
          disabled={!canGoNext}
        >
          <i className="fa-solid fa-chevron-right" />
        </Btn>
      </div>

      {/* ************************************************************************** */}

      {content}
    </section>
  )
}

export const MealsList = memo(FCMealsList)
